/**
 * A void somebody asked for, waiting for its second person.
 *
 * The line stays in the queue until a manager other than the requester confirms the
 * void with their authenticator code. That manager may also decline it, and the
 * requester may take it back; both say why. Who is offered what is decided in
 * `voidRequestStanding`, and the server refuses the rest whatever this screen shows.
 */
import { useState } from 'react';
import reconciliationService from '../../services/reconciliation.service';
import authService from '../../services/auth.service';
import type { BankTransaction, VoidRequest } from '../../services/bankTransactions.service';
import { formatDateTime } from '../../lib/datetime';
import { colors } from '../../styles/theme';
import {
  VOID_REASON_MAX,
  submitVoidConfirm,
  submitVoidDecline,
  voidProblem,
  voidRequestStanding,
  type VoidRequestStanding,
} from './bankRecon.helpers';

const inputStyle = { background: colors.canvas, color: colors.ink, border: `1px solid ${colors.border}` };

type Mode = 'confirm' | 'close' | null;

export default function VoidRequestPanel({ tx, request, onDone }: Readonly<{
  tx: Pick<BankTransaction, 'id'>;
  request: VoidRequest;
  onDone: (message: string) => void;
}>): JSX.Element {
  const [mode, setMode] = useState<Mode>(null);
  const [code, setCode] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    const result = mode === 'confirm'
      ? await submitVoidConfirm(reconciliationService, tx.id, code)
      : await submitVoidDecline(reconciliationService, tx.id, note);
    setBusy(false);
    if (result.ok) {
      onDone(result.message);
    } else {
      setError(result.error);
    }
  };

  return (
    <VoidRequestView
      idPrefix={tx.id}
      request={request}
      standing={voidRequestStanding(request, authService.getRole())}
      mode={mode}
      code={code}
      note={note}
      busy={busy}
      error={error}
      onMode={(next) => { setMode(next); setError(null); }}
      onCode={setCode}
      onNote={setNote}
      onRun={() => { void run(); }}
    />
  );
}

export function VoidRequestView({
  idPrefix, request, standing, mode, code, note, busy, error, onMode, onCode, onNote, onRun,
}: Readonly<{
  idPrefix: string;
  request: VoidRequest;
  standing: VoidRequestStanding;
  mode: Mode;
  code: string;
  note: string;
  busy: boolean;
  error: string | null;
  onMode: (mode: Mode) => void;
  onCode: (code: string) => void;
  onNote: (note: string) => void;
  onRun: () => void;
}>): JSX.Element {
  const noteProblem = voidProblem(note);
  const codeMissing = code.length < 6;
  return (
    <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
      <div className="rounded-lg p-3" style={{ border: `1px solid ${colors.warning}` }}>
        <p className="text-sm font-semibold" style={{ color: colors.ink }}>
          Void requested by {request.requested_by_name} · {formatDateTime(request.requested_at)}
        </p>
        <p className="text-sm mt-1" style={{ color: colors.inkSoft }}>&ldquo;{request.reason}&rdquo;</p>
        <p className="text-xs mt-2" style={{ color: colors.muted }}>{standing.note}</p>

        {error && (
          <div className="rounded-lg p-2 mt-3 text-xs" role="alert"
               style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
            {error}
          </div>
        )}

        {mode === null && (standing.mayConfirm || standing.mayClose) && (
          <div className="flex gap-2 mt-3">
            {standing.mayConfirm && (
              <button type="button" onClick={() => onMode('confirm')}
                      className="px-3 py-1.5 rounded-md text-xs font-semibold"
                      style={{ background: colors.danger, color: '#FFFFFF' }}>
                Confirm void
              </button>
            )}
            {standing.mayClose && (
              <button type="button" onClick={() => onMode('close')}
                      className="px-3 py-1.5 rounded-md text-xs font-semibold"
                      style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
                {standing.closeLabel}
              </button>
            )}
          </div>
        )}

        {mode === 'confirm' && standing.mayConfirm && (
          <div className="mt-3">
            <label htmlFor={`${idPrefix}-void-code`} className="block text-xs font-semibold mb-1"
                   style={{ color: colors.inkSoft }}>
              Your authentication code
            </label>
            <input id={`${idPrefix}-void-code`} type="text" inputMode="numeric"
                   autoComplete="one-time-code" value={code} disabled={busy}
                   onChange={(e) => onCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                   placeholder="6-digit code"
                   className="px-3 py-2 rounded-lg text-sm font-mono tracking-widest mb-2 block"
                   style={inputStyle} />
            {codeMissing && (
              <p className="text-xs mb-2" style={{ color: colors.warning }}>
                Enter the current code from your authenticator to confirm.
              </p>
            )}
            <div className="flex gap-2">
              <button type="button" disabled={busy || codeMissing} onClick={onRun}
                      className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                      style={{ background: colors.danger, color: '#FFFFFF' }}>
                {busy ? 'Voiding…' : 'Yes, void this line'}
              </button>
              <button type="button" disabled={busy} onClick={() => onMode(null)}
                      className="px-4 py-2 rounded-lg text-sm font-semibold"
                      style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
                Go back
              </button>
            </div>
          </div>
        )}

        {mode === 'close' && standing.mayClose && (
          <div className="mt-3">
            <label htmlFor={`${idPrefix}-void-note`} className="block text-xs font-semibold mb-1"
                   style={{ color: colors.inkSoft }}>
              Why is this line staying?
            </label>
            <textarea id={`${idPrefix}-void-note`} rows={2} value={note} disabled={busy}
                      onChange={(e) => onNote(e.target.value.slice(0, VOID_REASON_MAX))}
                      className="w-full px-3 py-2 rounded-lg text-sm mb-2" style={inputStyle} />
            {noteProblem && <p className="text-xs mb-2" style={{ color: colors.warning }}>{noteProblem}</p>}
            <div className="flex gap-2">
              <button type="button" disabled={busy || noteProblem !== null} onClick={onRun}
                      className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                      style={{ background: colors.navy, color: '#FFFFFF' }}>
                {busy ? 'Closing…' : standing.closeLabel}
              </button>
              <button type="button" disabled={busy} onClick={() => onMode(null)}
                      className="px-4 py-2 rounded-lg text-sm font-semibold"
                      style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
                Go back
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
